import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useParams, useNavigate, Link } from "react-router-dom";
import { 
  Phone, 
  Mail, 
  MessageCircle, 
  ChevronRight, 
  ChevronLeft,
  ShoppingCart, 
  Users, 
  Building2, 
  MapPin, 
  Globe,
  Zap,
  TrendingUp,
  Clock,
  DollarSign,
  Menu,
  X,
  Check,
  ArrowRight
} from "lucide-react";
import { Button } from "./components/ui/button";

// Client logos data
const clientLogos = [
  { name: "Suntech", url: "https://customer-assets.emergentagent.com/job_e96dab5f-2c5f-4b23-9051-8ead5acd1c12/artifacts/yg817qn6_logo.png" },
  { name: "Vallalar", url: "https://customer-assets.emergentagent.com/job_e96dab5f-2c5f-4b23-9051-8ead5acd1c12/artifacts/hdm7fn24_vvvsi-logo.jpg" },
  { name: "Seoul Impex", url: "https://customer-assets.emergentagent.com/job_e96dab5f-2c5f-4b23-9051-8ead5acd1c12/artifacts/5dzb6ouy_xdabh49j_logo-beta%20%281%29.png" },
  { name: "UVC", url: "https://customer-assets.emergentagent.com/job_e96dab5f-2c5f-4b23-9051-8ead5acd1c12/artifacts/gq3yd5n9_yuvi-copy.png" },
  { name: "Fortuna", url: "https://customer-assets.emergentagent.com/job_e96dab5f-2c5f-4b23-9051-8ead5acd1c12/artifacts/63ucmwya_fortuna_logo.png" }
];

// Solutions data with full details
const solutions = [
  {
    id: "gpos-ai",
    title: "GPOS AI",
    subtitle: "Smart Billing & Inventory SaaS",
    description: "A complete solution for retail stores, distributors, and agencies.",
    fullDescription: "GPOS AI is a comprehensive, AI-powered Point of Sale and Inventory Management system designed specifically for retail stores, distributors, and agencies. Our smart billing solution leverages artificial intelligence to streamline your business operations, reduce errors, and increase profitability.",
    features: ["Billing & invoicing", "Inventory & stock management", "Sales tracking & analytics", "Easy setup and usage"],
    detailedFeatures: [
      { title: "Smart Billing", description: "AI-assisted billing with automatic tax calculations, discount management, and multiple payment modes" },
      { title: "Inventory Management", description: "Real-time stock tracking, low stock alerts, and automated reorder suggestions" },
      { title: "Sales Analytics", description: "Comprehensive dashboards with sales trends, top products, and customer insights" },
      { title: "Multi-store Support", description: "Manage multiple outlets from a single dashboard with consolidated reporting" },
      { title: "Barcode Integration", description: "Quick product scanning and barcode generation for efficient operations" },
      { title: "Customer Management", description: "Track customer purchases, manage loyalty programs, and send promotional offers" }
    ],
    price: "Starting from ₹199/month",
    icon: ShoppingCart,
    benefits: ["Reduce billing errors by 95%", "Save 2+ hours daily on inventory", "Increase sales visibility", "24/7 cloud access"]
  },
  {
    id: "jamunhr",
    title: "JamunHR",
    subtitle: "Smart HRMS Platform",
    description: "A modern SaaS-based HRMS solution for all industries.",
    fullDescription: "JamunHR is a next-generation Human Resource Management System that simplifies and automates all your HR processes. From onboarding to offboarding, attendance to payroll, JamunHR handles everything with precision and ease, allowing you to focus on what matters most - your people.",
    features: ["Employee onboarding", "Attendance & shift management", "Payroll processing", "Compliance management"],
    detailedFeatures: [
      { title: "Digital Onboarding", description: "Paperless employee onboarding with document management and e-signatures" },
      { title: "Attendance System", description: "Biometric integration, geo-fencing, and mobile check-in for accurate tracking" },
      { title: "Shift Management", description: "Flexible shift scheduling, swap requests, and overtime calculations" },
      { title: "Payroll Automation", description: "Automated salary processing with tax deductions, reimbursements, and compliance" },
      { title: "Leave Management", description: "Custom leave policies, approval workflows, and balance tracking" },
      { title: "Performance Reviews", description: "Goal setting, 360-degree feedback, and performance analytics" }
    ],
    price: "Affordable and scalable",
    icon: Users,
    benefits: ["Save 60% on HR admin time", "Zero payroll errors", "100% statutory compliance", "Employee self-service portal"]
  },
  {
    id: "field-service",
    title: "Field Service Management",
    subtitle: "AI-Powered Field Operations",
    description: "AI-powered solution for service and sales-based companies.",
    fullDescription: "Our Field Service Management system empowers your field workforce with mobile-first tools for task management, real-time tracking, and customer service excellence. Powered by AI, it optimizes routes, predicts service needs, and ensures your field team operates at peak efficiency.",
    features: ["Mobile app for field executives", "Task & service tracking", "Customer management", "Real-time updates"],
    detailedFeatures: [
      { title: "Mobile App", description: "Native Android & iOS apps for field executives with offline capability" },
      { title: "GPS Tracking", description: "Real-time location tracking with route optimization and geo-fencing" },
      { title: "Task Assignment", description: "Smart task allocation based on location, skill, and availability" },
      { title: "Service Reports", description: "Digital service reports with photos, signatures, and instant sharing" },
      { title: "Customer Portal", description: "Self-service portal for customers to track service status and history" },
      { title: "Analytics Dashboard", description: "Performance metrics, SLA tracking, and productivity insights" }
    ],
    price: "Complete operational control",
    icon: MapPin,
    benefits: ["30% faster service completion", "Real-time visibility", "Improved customer satisfaction", "Reduced fuel costs"]
  },
  {
    id: "multi-chain",
    title: "Multi-Chain Retail",
    subtitle: "Enterprise Management System",
    description: "A robust enterprise solution for multi-branch and franchise businesses.",
    fullDescription: "Multi-Chain Retail Management System is built for enterprises operating multiple stores, warehouses, and franchises across different locations. It provides centralized control while allowing local flexibility, ensuring smooth operations across your entire retail network.",
    features: ["Multi-store & warehouse management", "Centralized HQ control", "Cross-state operations", "Advanced analytics"],
    detailedFeatures: [
      { title: "Central Dashboard", description: "Single view of all stores, warehouses, and operations across locations" },
      { title: "Inventory Sync", description: "Real-time inventory synchronization across all outlets and warehouses" },
      { title: "Franchise Management", description: "Royalty calculations, brand compliance, and franchisee performance tracking" },
      { title: "Inter-branch Transfers", description: "Seamless stock transfers between locations with automatic documentation" },
      { title: "Multi-state Compliance", description: "GST compliance for inter-state and intra-state transactions" },
      { title: "Consolidated Reporting", description: "Enterprise-wide reports with drill-down to individual store level" }
    ],
    price: "Enterprise solution",
    icon: Building2,
    benefits: ["Unified brand experience", "Optimized inventory across chain", "Reduced stockouts by 40%", "Complete franchise control"]
  },
  {
    id: "web-portals",
    title: "Custom Web Portals",
    subtitle: "High-Performance Solutions",
    description: "We develop and upgrade high-performance web portals.",
    fullDescription: "We specialize in developing and modernizing custom web portals that drive business growth. Whether you need to fix legacy system issues, build new customer portals, or integrate AI capabilities, our team delivers fast, secure, and scalable solutions tailored to your specific needs.",
    features: ["Fixing legacy system issues", "Rapid deployment within days", "AI-driven enhancements", "Continuous support"],
    detailedFeatures: [
      { title: "Legacy Modernization", description: "Upgrade outdated systems to modern, scalable architectures" },
      { title: "Custom Development", description: "Bespoke portals designed around your unique business workflows" },
      { title: "AI Integration", description: "Add intelligent features like chatbots, recommendations, and automation" },
      { title: "API Development", description: "RESTful APIs for seamless integration with existing systems" },
      { title: "Performance Optimization", description: "Speed optimization, caching, and CDN implementation" },
      { title: "Security Hardening", description: "SSL, authentication, authorization, and vulnerability patching" }
    ],
    price: "Tailored to your needs",
    icon: Globe,
    benefits: ["Fast turnaround time", "Scalable architecture", "AI-ready infrastructure", "Ongoing maintenance support"]
  }
];

// What we do benefits
const benefits = [
  { icon: Zap, title: "Faster Decision-Making", description: "AI-powered insights for quick, informed decisions" },
  { icon: TrendingUp, title: "Improved Efficiency", description: "Streamlined operations and optimized workflows" },
  { icon: Clock, title: "Simplified Workflows", description: "Automated processes that save time" },
  { icon: DollarSign, title: "Reduced Costs", description: "Lower operational expenses through automation" }
];

// Header Component
const Header = ({ isHome = true }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = isHome ? [
    { href: "#who-we-are", label: "Who We Are" },
    { href: "#solutions", label: "Solutions" },
    { href: "#clients", label: "Clients" },
    { href: "#contact", label: "Contact" }
  ] : [
    { href: "/#who-we-are", label: "Who We Are" },
    { href: "/#solutions", label: "Solutions" },
    { href: "/#clients", label: "Clients" },
    { href: "/#contact", label: "Contact" }
  ];

  const handleNavClick = (e, href) => {
    if (!isHome && href.startsWith('/#')) {
      e.preventDefault();
      navigate('/');
      setTimeout(() => {
        const element = document.querySelector(href.replace('/', ''));
        element?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  return (
    <header 
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md border-b border-[#E4E4E7] shadow-sm' : 'bg-white'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" data-testid="logo" className="font-heading text-xl md:text-2xl font-semibold text-[#0A0A0A]">
            <span className="text-[#002FA7]">Livicot</span> Technologies
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                className="text-sm font-medium text-[#52525B] hover:text-[#002FA7] transition-colors"
              >
                {link.label}
              </a>
            ))}
            <Button 
              data-testid="header-cta"
              className="bg-[#002FA7] hover:bg-[#001f75] text-white rounded-sm px-6"
              onClick={() => window.open('https://wa.me/916382947171', '_blank')}
            >
              Get Started
            </Button>
          </nav>

          <button
            data-testid="mobile-menu-btn"
            className="md:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {isMobileMenuOpen && (
          <nav data-testid="mobile-nav" className="md:hidden pb-6 border-t border-[#E4E4E7]">
            <div className="flex flex-col gap-4 pt-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => {
                    handleNavClick(e, link.href);
                    setIsMobileMenuOpen(false);
                  }}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className="text-base font-medium text-[#52525B] hover:text-[#002FA7] transition-colors"
                >
                  {link.label}
                </a>
              ))}
              <Button 
                data-testid="mobile-header-cta"
                className="bg-[#002FA7] hover:bg-[#001f75] text-white rounded-sm w-full mt-2"
                onClick={() => window.open('https://wa.me/916382947171', '_blank')}
              >
                Get Started
              </Button>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

// Hero Section - Reduced padding
const HeroSection = () => {
  return (
    <section data-testid="hero-section" className="pt-20 md:pt-24 pb-12 md:pb-16 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div className="fade-in">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#52525B] mb-3 block">
              AI-Powered Solutions
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#0A0A0A] mb-5 leading-[1.1]">
              AI-Powered Custom<br />
              <span className="text-[#002FA7]">Software Solutions</span>
            </h1>
            <p className="text-base md:text-lg text-[#52525B] leading-relaxed mb-6 max-w-xl">
              We specialize in delivering AI-driven customized software solutions that transform businesses with speed, precision, and scalability. Results up to <span className="font-semibold text-[#002FA7]">100x faster</span> with exceptional accuracy.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button 
                data-testid="hero-cta-primary"
                className="bg-[#002FA7] hover:bg-[#001f75] text-white rounded-sm px-8 py-5 text-base"
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Contact Us <ChevronRight className="ml-2" size={18} />
              </Button>
              <Button 
                data-testid="hero-cta-secondary"
                variant="outline"
                className="border-[#E4E4E7] hover:border-[#002FA7] hover:text-[#002FA7] rounded-sm px-8 py-5 text-base"
                onClick={() => document.getElementById('solutions')?.scrollIntoView({ behavior: 'smooth' })}
              >
                View Solutions
              </Button>
            </div>
          </div>

          <div className="hidden lg:block">
            <div className="relative">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/e96dab5f-2c5f-4b23-9051-8ead5acd1c12/images/0b4eb51d40f92149083eb89e3b6948a785388dcb78c0dcfd9a2d62df338aaa12.png"
                alt="AI Technology"
                className="w-full h-auto object-cover"
                data-testid="hero-image"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Who We Are Section - Reduced padding
const WhoWeAreSection = () => {
  return (
    <section id="who-we-are" data-testid="who-we-are-section" className="py-14 md:py-20 bg-[#F5F5F7] border-y border-[#E4E4E7]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          <div>
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#52525B] mb-4 block">
              Who We Are
            </span>
            <div className="mb-6">
              <span className="font-mono text-6xl md:text-7xl lg:text-8xl font-semibold text-[#002FA7] leading-none">20+</span>
              <p className="font-heading text-xl md:text-2xl font-medium text-[#0A0A0A] mt-3">Years of Industry Expertise</p>
            </div>
            <div className="flex gap-8 md:gap-10">
              <div>
                <span className="font-mono text-2xl md:text-3xl font-semibold text-[#0A0A0A]">50+</span>
                <p className="text-sm text-[#52525B] mt-1">Global Experts</p>
              </div>
              <div>
                <span className="font-mono text-2xl md:text-3xl font-semibold text-[#0A0A0A]">100+</span>
                <p className="text-sm text-[#52525B] mt-1">Projects Delivered</p>
              </div>
            </div>
          </div>

          <div className="lg:pt-6">
            <p className="text-base md:text-lg text-[#0A0A0A] leading-relaxed mb-5">
              We are a team of highly experienced IT professionals supported by a strong network of global technology experts and AI research & analysis specialists.
            </p>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-[#002FA7] rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-[#52525B] text-sm"><span className="font-medium text-[#0A0A0A]">Deep technical knowledge</span> across multiple technology stacks</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-[#002FA7] rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-[#52525B] text-sm"><span className="font-medium text-[#0A0A0A]">Real-world business understanding</span> to solve practical challenges</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-[#002FA7] rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-[#52525B] text-sm"><span className="font-medium text-[#0A0A0A]">Advanced AI innovation</span> for future-ready solutions</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// What We Do Section - Reduced padding
const WhatWeDoSection = () => {
  return (
    <section id="what-we-do" data-testid="what-we-do-section" className="py-14 md:py-20 bg-white relative">
      <div 
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `url("https://static.prod-images.emergentagent.com/jobs/e96dab5f-2c5f-4b23-9051-8ead5acd1c12/images/7bd24d089becc0a5d771b3ab8893841fe1b88551f03de7783d943c4e9616407e.png")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="max-w-3xl mb-10">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#52525B] mb-3 block">
            What We Do
          </span>
          <h2 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-[#0A0A0A] mb-4">
            AI-Driven Business Automation
          </h2>
          <p className="text-base text-[#52525B] leading-relaxed">
            We provide AI-driven business automation solutions designed to simplify and optimize your operations. Our focus is on solving complex business management challenges, automating manual processes, and delivering fast, reliable, and scalable solutions.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {benefits.map((benefit, index) => (
            <div 
              key={index}
              data-testid={`benefit-${index}`}
              className="p-5 border border-[#E4E4E7] bg-white hover:border-[#002FA7] transition-colors group"
            >
              <benefit.icon className="w-7 h-7 text-[#002FA7] mb-3" />
              <h3 className="font-heading text-base font-medium text-[#0A0A0A] mb-1">{benefit.title}</h3>
              <p className="text-sm text-[#52525B]">{benefit.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Solutions Section - Reduced padding
const SolutionsSection = () => {
  return (
    <section id="solutions" data-testid="solutions-section" className="py-14 md:py-20 bg-white border-t border-[#E4E4E7]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-10">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#52525B] mb-3 block">
            Our Solutions
          </span>
          <h2 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-[#0A0A0A]">
            Products & Services
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 border border-[#E4E4E7]">
          {solutions.map((solution, index) => (
            <Link 
              to={`/solution/${solution.id}`}
              key={solution.id}
              data-testid={`solution-${solution.id}`}
              className={`p-6 border-b border-r border-[#E4E4E7] hover:border-[#002FA7] transition-all group bg-white hover:bg-[#F5F5F7] block ${
                index === solutions.length - 1 ? 'lg:col-span-1' : ''
              }`}
            >
              <solution.icon className="w-8 h-8 text-[#002FA7] mb-4" />
              <span className="font-mono text-xs uppercase tracking-[0.15em] text-[#52525B] block mb-1">
                {solution.subtitle}
              </span>
              <h3 className="font-heading text-lg font-medium text-[#0A0A0A] mb-2 group-hover:text-[#002FA7] transition-colors flex items-center gap-2">
                {solution.title}
                <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
              </h3>
              <p className="text-sm text-[#52525B] mb-3">{solution.description}</p>
              <ul className="space-y-1.5 mb-4">
                {solution.features.slice(0, 3).map((feature, fIndex) => (
                  <li key={fIndex} className="flex items-center gap-2 text-sm text-[#52525B]">
                    <div className="w-1.5 h-1.5 bg-[#002FA7] rounded-full"></div>
                    {feature}
                  </li>
                ))}
              </ul>
              <p className="font-mono text-xs text-[#002FA7] font-medium">{solution.price}</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

// Clients Section - Reduced padding
const ClientsSection = () => {
  return (
    <section id="clients" data-testid="clients-section" className="py-12 md:py-16 bg-[#F5F5F7] border-t border-[#E4E4E7]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-8">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#52525B] mb-3 block">
            Our Clients
          </span>
          <h2 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-[#0A0A0A] mb-3">
            Trusted by Businesses
          </h2>
          <p className="text-sm text-[#52525B] max-w-2xl mx-auto">
            We have successfully delivered solutions to clients across industries, helping them streamline operations and scale using AI-powered systems.
          </p>
        </div>

        <div className="marquee-container py-6">
          <div className="marquee-content">
            {[...clientLogos, ...clientLogos].map((client, index) => (
              <div 
                key={`${client.name}-${index}`}
                data-testid={`client-logo-${index}`}
                className="flex-shrink-0 mx-6 md:mx-10 flex items-center justify-center"
              >
                <img 
                  src={client.url} 
                  alt={client.name}
                  className="h-14 md:h-16 w-auto object-contain grayscale hover:grayscale-0 transition-all opacity-70 hover:opacity-100"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Section - Reduced padding
const ContactSection = () => {
  return (
    <section id="contact" data-testid="contact-section" className="py-14 md:py-20 bg-white border-t border-[#E4E4E7]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
          <div>
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#52525B] mb-4 block">
              Contact Us
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#0A0A0A] mb-4">
              Let's <span className="text-[#002FA7]">Talk</span>
            </h2>
            <p className="text-base text-[#52525B] leading-relaxed max-w-md">
              Ready to transform your business with AI-powered solutions? Get in touch with us today.
            </p>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="font-heading text-lg font-medium text-[#0A0A0A] mb-4">Livicot Technologies</h3>
              <div className="space-y-3">
                <a 
                  href="tel:+916382947171"
                  data-testid="contact-phone"
                  className="flex items-center gap-3 text-[#0A0A0A] hover:text-[#002FA7] transition-colors group"
                >
                  <div className="w-10 h-10 border border-[#E4E4E7] flex items-center justify-center group-hover:border-[#002FA7] transition-colors">
                    <Phone className="w-4 h-4" />
                  </div>
                  <span className="font-mono text-base">+91 63829 47171</span>
                </a>
                <a 
                  href="mailto:info@livicot.com"
                  data-testid="contact-email"
                  className="flex items-center gap-3 text-[#0A0A0A] hover:text-[#002FA7] transition-colors group"
                >
                  <div className="w-10 h-10 border border-[#E4E4E7] flex items-center justify-center group-hover:border-[#002FA7] transition-colors">
                    <Mail className="w-4 h-4" />
                  </div>
                  <span className="font-mono text-base">info@livicot.com</span>
                </a>
              </div>
            </div>

            <div className="pt-2">
              <Button 
                data-testid="contact-whatsapp-btn"
                className="bg-[#25D366] hover:bg-[#1da851] text-white rounded-sm px-6 py-5 text-base w-full sm:w-auto"
                onClick={() => window.open('https://wa.me/916382947171', '_blank')}
              >
                <MessageCircle className="mr-2" size={18} />
                Chat on WhatsApp
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer data-testid="footer" className="py-6 bg-[#0A0A0A] text-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-sm text-white/60">
            © {new Date().getFullYear()} Livicot Technologies. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/privacy-policy" data-testid="footer-privacy" className="text-sm text-white/60 hover:text-white transition-colors">Privacy Policy</Link>
            <a href="#" className="text-sm text-white/60 hover:text-white transition-colors">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// WhatsApp Floating Button
const WhatsAppFloat = () => {
  return (
    <a
      href="https://wa.me/916382947171"
      target="_blank"
      rel="noopener noreferrer"
      data-testid="whatsapp-float"
      className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#25D366] hover:bg-[#1da851] rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all group"
      aria-label="Chat on WhatsApp"
    >
      <MessageCircle className="w-7 h-7 text-white" />
      <span className="absolute right-full mr-3 bg-white text-[#0A0A0A] text-sm font-medium px-3 py-2 rounded-lg shadow-md opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
        Chat with us
      </span>
    </a>
  );
};

// Solution Detail Page Component
const SolutionDetailPage = () => {
  const { solutionId } = useParams();
  const navigate = useNavigate();
  const solution = solutions.find(s => s.id === solutionId);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [solutionId]);

  if (!solution) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-2xl font-medium text-[#0A0A0A] mb-4">Solution not found</h1>
          <Button onClick={() => navigate('/')} className="bg-[#002FA7] hover:bg-[#001f75] text-white">
            Go Back Home
          </Button>
        </div>
      </div>
    );
  }

  const currentIndex = solutions.findIndex(s => s.id === solutionId);
  const prevSolution = currentIndex > 0 ? solutions[currentIndex - 1] : null;
  const nextSolution = currentIndex < solutions.length - 1 ? solutions[currentIndex + 1] : null;

  return (
    <div className="min-h-screen bg-white">
      <Header isHome={false} />
      
      {/* Hero Section */}
      <section className="pt-24 md:pt-28 pb-12 md:pb-16 bg-white border-b border-[#E4E4E7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <button 
            onClick={() => navigate('/#solutions')}
            data-testid="back-to-solutions"
            className="flex items-center gap-2 text-[#52525B] hover:text-[#002FA7] transition-colors mb-6 text-sm"
          >
            <ChevronLeft size={16} />
            Back to Solutions
          </button>
          
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <div>
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 bg-[#F5F5F7] border border-[#E4E4E7] flex items-center justify-center">
                  <solution.icon className="w-7 h-7 text-[#002FA7]" />
                </div>
                <span className="font-mono text-xs uppercase tracking-[0.15em] text-[#52525B]">
                  {solution.subtitle}
                </span>
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#0A0A0A] mb-4">
                {solution.title}
              </h1>
              <p className="text-base md:text-lg text-[#52525B] leading-relaxed mb-6">
                {solution.fullDescription}
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  data-testid="solution-cta-primary"
                  className="bg-[#002FA7] hover:bg-[#001f75] text-white rounded-sm px-6 py-5"
                  onClick={() => window.open('https://wa.me/916382947171', '_blank')}
                >
                  Get Started <ChevronRight className="ml-2" size={18} />
                </Button>
                <Button 
                  data-testid="solution-cta-secondary"
                  variant="outline"
                  className="border-[#E4E4E7] hover:border-[#002FA7] hover:text-[#002FA7] rounded-sm px-6 py-5"
                  onClick={() => navigate('/#contact')}
                >
                  Contact Sales
                </Button>
              </div>
            </div>

            <div className="bg-[#F5F5F7] p-6 border border-[#E4E4E7]">
              <h3 className="font-heading text-lg font-medium text-[#0A0A0A] mb-4">Key Benefits</h3>
              <ul className="space-y-3">
                {solution.benefits.map((benefit, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-[#002FA7] rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-[#0A0A0A] text-sm">{benefit}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 pt-4 border-t border-[#E4E4E7]">
                <p className="font-mono text-sm text-[#002FA7] font-semibold">{solution.price}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-10">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#52525B] mb-3 block">
              Features
            </span>
            <h2 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-[#0A0A0A]">
              What's Included
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {solution.detailedFeatures.map((feature, index) => (
              <div 
                key={index}
                data-testid={`feature-${index}`}
                className="p-5 border border-[#E4E4E7] bg-white hover:border-[#002FA7] transition-colors"
              >
                <h3 className="font-heading text-base font-medium text-[#0A0A0A] mb-2">{feature.title}</h3>
                <p className="text-sm text-[#52525B]">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-16 bg-[#002FA7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-medium text-white mb-4">
            Ready to get started with {solution.title}?
          </h2>
          <p className="text-white/80 mb-6 max-w-xl mx-auto">
            Contact us today for a free consultation and demo. Let us show you how {solution.title} can transform your business.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button 
              className="bg-white text-[#002FA7] hover:bg-gray-100 rounded-sm px-6 py-5"
              onClick={() => window.open('https://wa.me/916382947171', '_blank')}
            >
              <MessageCircle className="mr-2" size={18} />
              Chat on WhatsApp
            </Button>
            <Button 
              variant="outline"
              className="border-white text-white hover:bg-white/10 rounded-sm px-6 py-5"
              onClick={() => navigate('/#contact')}
            >
              Contact Us
            </Button>
          </div>
        </div>
      </section>

      {/* Navigation to Other Solutions */}
      <section className="py-10 md:py-14 bg-[#F5F5F7] border-t border-[#E4E4E7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex justify-between items-center">
            {prevSolution ? (
              <Link 
                to={`/solution/${prevSolution.id}`}
                data-testid="prev-solution"
                className="flex items-center gap-3 text-[#52525B] hover:text-[#002FA7] transition-colors group"
              >
                <ChevronLeft size={20} />
                <div className="text-left">
                  <span className="text-xs uppercase tracking-wider block">Previous</span>
                  <span className="font-heading font-medium text-[#0A0A0A] group-hover:text-[#002FA7]">{prevSolution.title}</span>
                </div>
              </Link>
            ) : <div />}
            
            {nextSolution ? (
              <Link 
                to={`/solution/${nextSolution.id}`}
                data-testid="next-solution"
                className="flex items-center gap-3 text-[#52525B] hover:text-[#002FA7] transition-colors group text-right"
              >
                <div>
                  <span className="text-xs uppercase tracking-wider block">Next</span>
                  <span className="font-heading font-medium text-[#0A0A0A] group-hover:text-[#002FA7]">{nextSolution.title}</span>
                </div>
                <ChevronRight size={20} />
              </Link>
            ) : <div />}
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
};

// Privacy Policy Page Component
const PrivacyPolicyPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header isHome={false} />
      
      <section className="pt-24 md:pt-28 pb-14 md:pb-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="mb-10">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#52525B] mb-3 block">
              Legal
            </span>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#0A0A0A] mb-4">
              Privacy Policy
            </h1>
            <p className="text-[#52525B]">
              Last updated: {new Date().toLocaleDateString('en-IN', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </div>

          <div className="prose prose-lg max-w-none">
            {/* Introduction */}
            <div className="mb-8">
              <h2 className="font-heading text-xl font-medium text-[#0A0A0A] mb-3">1. Introduction</h2>
              <p className="text-[#52525B] leading-relaxed mb-4">
                Livicot Technologies ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website, use our software products (GPOS AI, JamunHR, Field Service Management, Multi-Chain Retail Management, and Custom Web Portals), or engage with our services.
              </p>
              <p className="text-[#52525B] leading-relaxed">
                Please read this Privacy Policy carefully. By accessing or using our services, you acknowledge that you have read, understood, and agree to be bound by this Privacy Policy.
              </p>
            </div>

            {/* Information We Collect */}
            <div className="mb-8">
              <h2 className="font-heading text-xl font-medium text-[#0A0A0A] mb-3">2. Information We Collect</h2>
              
              <h3 className="font-heading text-lg font-medium text-[#0A0A0A] mb-2 mt-4">2.1 Personal Information</h3>
              <p className="text-[#52525B] leading-relaxed mb-3">We may collect personal information that you voluntarily provide, including:</p>
              <ul className="list-disc pl-6 text-[#52525B] space-y-2 mb-4">
                <li>Name and contact details (email address, phone number, business address)</li>
                <li>Company/Organization name and designation</li>
                <li>Billing and payment information</li>
                <li>Login credentials for our software platforms</li>
                <li>Communication preferences</li>
              </ul>

              <h3 className="font-heading text-lg font-medium text-[#0A0A0A] mb-2 mt-4">2.2 Business Data</h3>
              <p className="text-[#52525B] leading-relaxed mb-3">When you use our software solutions, we may process:</p>
              <ul className="list-disc pl-6 text-[#52525B] space-y-2 mb-4">
                <li>Transaction and billing records (GPOS AI)</li>
                <li>Employee and HR data (JamunHR)</li>
                <li>Customer and service data (Field Service Management)</li>
                <li>Inventory and sales data (Multi-Chain Retail)</li>
                <li>Any data you input into our custom web portals</li>
              </ul>

              <h3 className="font-heading text-lg font-medium text-[#0A0A0A] mb-2 mt-4">2.3 Automatically Collected Information</h3>
              <ul className="list-disc pl-6 text-[#52525B] space-y-2">
                <li>Device information (IP address, browser type, operating system)</li>
                <li>Usage data (pages visited, features used, time spent)</li>
                <li>Cookies and similar tracking technologies</li>
                <li>Log files and analytics data</li>
              </ul>
            </div>

            {/* How We Use Information */}
            <div className="mb-8">
              <h2 className="font-heading text-xl font-medium text-[#0A0A0A] mb-3">3. How We Use Your Information</h2>
              <p className="text-[#52525B] leading-relaxed mb-3">We use the collected information for:</p>
              <ul className="list-disc pl-6 text-[#52525B] space-y-2">
                <li>Providing, maintaining, and improving our software services</li>
                <li>Processing transactions and sending related information</li>
                <li>Responding to inquiries and providing customer support</li>
                <li>Sending administrative information, updates, and promotional communications</li>
                <li>Analyzing usage patterns to enhance user experience</li>
                <li>Detecting, preventing, and addressing technical issues or security breaches</li>
                <li>Complying with legal obligations and enforcing our terms</li>
                <li>Training AI models to improve our services (using anonymized data only)</li>
              </ul>
            </div>

            {/* Data Security */}
            <div className="mb-8">
              <h2 className="font-heading text-xl font-medium text-[#0A0A0A] mb-3">4. Data Security</h2>
              <p className="text-[#52525B] leading-relaxed mb-4">
                We implement industry-standard security measures to protect your information, including:
              </p>
              <ul className="list-disc pl-6 text-[#52525B] space-y-2">
                <li>SSL/TLS encryption for data transmission</li>
                <li>Encrypted database storage for sensitive information</li>
                <li>Regular security audits and vulnerability assessments</li>
                <li>Access controls and authentication mechanisms</li>
                <li>Secure cloud infrastructure with regular backups</li>
                <li>Employee training on data protection practices</li>
              </ul>
              <p className="text-[#52525B] leading-relaxed mt-4">
                While we strive to use commercially acceptable means to protect your data, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security.
              </p>
            </div>

            {/* Data Sharing */}
            <div className="mb-8">
              <h2 className="font-heading text-xl font-medium text-[#0A0A0A] mb-3">5. Data Sharing and Disclosure</h2>
              <p className="text-[#52525B] leading-relaxed mb-3">We may share your information with:</p>
              <ul className="list-disc pl-6 text-[#52525B] space-y-2">
                <li><strong>Service Providers:</strong> Third-party vendors who assist in operating our services (hosting, payment processing, analytics)</li>
                <li><strong>Business Partners:</strong> With your consent, for joint offerings or integrations</li>
                <li><strong>Legal Requirements:</strong> When required by law, court order, or government request</li>
                <li><strong>Business Transfers:</strong> In connection with mergers, acquisitions, or sale of assets</li>
                <li><strong>Protection of Rights:</strong> To protect our rights, privacy, safety, or property</li>
              </ul>
              <p className="text-[#52525B] leading-relaxed mt-4">
                <strong>We do not sell your personal information to third parties.</strong>
              </p>
            </div>

            {/* Data Retention */}
            <div className="mb-8">
              <h2 className="font-heading text-xl font-medium text-[#0A0A0A] mb-3">6. Data Retention</h2>
              <p className="text-[#52525B] leading-relaxed">
                We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. Business data in our SaaS products is retained according to your subscription terms and can be exported or deleted upon request.
              </p>
            </div>

            {/* Your Rights */}
            <div className="mb-8">
              <h2 className="font-heading text-xl font-medium text-[#0A0A0A] mb-3">7. Your Rights</h2>
              <p className="text-[#52525B] leading-relaxed mb-3">You have the right to:</p>
              <ul className="list-disc pl-6 text-[#52525B] space-y-2">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Correction:</strong> Request correction of inaccurate data</li>
                <li><strong>Deletion:</strong> Request deletion of your data (subject to legal requirements)</li>
                <li><strong>Portability:</strong> Receive your data in a structured, machine-readable format</li>
                <li><strong>Objection:</strong> Object to processing of your data for certain purposes</li>
                <li><strong>Withdraw Consent:</strong> Withdraw consent where processing is based on consent</li>
              </ul>
              <p className="text-[#52525B] leading-relaxed mt-4">
                To exercise these rights, please contact us at <a href="mailto:info@livicot.com" className="text-[#002FA7] hover:underline">info@livicot.com</a>.
              </p>
            </div>

            {/* Cookies */}
            <div className="mb-8">
              <h2 className="font-heading text-xl font-medium text-[#0A0A0A] mb-3">8. Cookies and Tracking Technologies</h2>
              <p className="text-[#52525B] leading-relaxed mb-4">
                We use cookies and similar technologies to enhance your experience. These include:
              </p>
              <ul className="list-disc pl-6 text-[#52525B] space-y-2">
                <li><strong>Essential Cookies:</strong> Required for basic site functionality</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our site</li>
                <li><strong>Functional Cookies:</strong> Remember your preferences and settings</li>
              </ul>
              <p className="text-[#52525B] leading-relaxed mt-4">
                You can control cookies through your browser settings. Disabling cookies may affect the functionality of our services.
              </p>
            </div>

            {/* Third-Party Links */}
            <div className="mb-8">
              <h2 className="font-heading text-xl font-medium text-[#0A0A0A] mb-3">9. Third-Party Links</h2>
              <p className="text-[#52525B] leading-relaxed">
                Our website and services may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to review the privacy policies of any third-party sites you visit.
              </p>
            </div>

            {/* Children's Privacy */}
            <div className="mb-8">
              <h2 className="font-heading text-xl font-medium text-[#0A0A0A] mb-3">10. Children's Privacy</h2>
              <p className="text-[#52525B] leading-relaxed">
                Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected data from a child, we will take steps to delete it promptly.
              </p>
            </div>

            {/* Changes to Policy */}
            <div className="mb-8">
              <h2 className="font-heading text-xl font-medium text-[#0A0A0A] mb-3">11. Changes to This Privacy Policy</h2>
              <p className="text-[#52525B] leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. Continued use of our services after changes constitutes acceptance of the updated policy.
              </p>
            </div>

            {/* Contact Information */}
            <div className="mb-8 p-6 bg-[#F5F5F7] border border-[#E4E4E7]">
              <h2 className="font-heading text-xl font-medium text-[#0A0A0A] mb-3">12. Contact Us</h2>
              <p className="text-[#52525B] leading-relaxed mb-4">
                If you have any questions or concerns about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="space-y-2 text-[#0A0A0A]">
                <p><strong>Livicot Technologies</strong></p>
                <p>Email: <a href="mailto:info@livicot.com" className="text-[#002FA7] hover:underline">info@livicot.com</a></p>
                <p>Phone: <a href="tel:+916382947171" className="text-[#002FA7] hover:underline">+91 63829 47171</a></p>
                <p>WhatsApp: <a href="https://wa.me/916382947171" target="_blank" rel="noopener noreferrer" className="text-[#002FA7] hover:underline">Chat with us</a></p>
              </div>
            </div>

          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
};

// Home Page Component
const HomePage = () => {
  return (
    <div className="font-body">
      <Header isHome={true} />
      <main>
        <HeroSection />
        <WhoWeAreSection />
        <WhatWeDoSection />
        <SolutionsSection />
        <ClientsSection />
        <ContactSection />
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
};

// Main App Component
function App() {
  return (
    <div className="App font-body">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/solution/:solutionId" element={<SolutionDetailPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
